import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { formatWalletBalanceMinor } from '../../services/channelService';
import {
  getInternalNonUserWallets,
  getInternalWalletB2BPing,
  isInternalWalletApiConfigured,
} from '../../services/internalWalletService';
import { getWelcomeGreeting, logoutRemote } from '../../services/authService';
import { getInternalErrorMessage } from '../../services/internalApi';
import InternalApiUnreachable from './InternalApiUnreachable';

const DEFAULT_LIMIT = 50;
const STATUS_OPTIONS = [
  { value: '', label: 'Any' },
  { value: 'ACTIVE', label: 'ACTIVE' },
  { value: 'BLOCKED', label: 'BLOCKED' },
  { value: 'CLOSED', label: 'CLOSED' },
];

/** Search Wallet — internal / VPN: find wallets with filters and pagination. */
function NonUserWalletsPage() {
  const navigate = useNavigate();
  const [pingStatus, setPingStatus] = useState(() => (isInternalWalletApiConfigured() ? 'loading' : 'skipped'));
  const [pingPayload, setPingPayload] = useState(null);

  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [ownerType, setOwnerType] = useState('');
  const [currency, setCurrency] = useState('');
  const [status, setStatus] = useState('');
  const [walletIdFilter, setWalletIdFilter] = useState('');
  const [limit, setLimit] = useState(String(DEFAULT_LIMIT));

  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loggingOut, setLoggingOut] = useState(false);

  const runPing = useCallback(async () => {
    if (!isInternalWalletApiConfigured()) {
      setPingStatus('skipped');
      setPingPayload(null);
      return;
    }
    setPingStatus('loading');
    setPingPayload(null);
    try {
      const data = await getInternalWalletB2BPing();
      setPingPayload(data && typeof data === 'object' ? data : {});
      setPingStatus('ready');
    } catch (e) {
      console.error(e);
      setPingStatus('unreachable');
      setPingPayload(null);
    }
  }, []);

  useEffect(() => {
    runPing();
  }, [runPing]);

  const listAllowed =
    pingStatus === 'ready' &&
    pingPayload?.accessible !== false &&
    pingPayload?.features?.list_other_user_wallets === true;

  const limitParsed = useMemo(() => {
    const n = Number.parseInt(String(limit).trim(), 10);
    if (!Number.isFinite(n) || n < 1) return DEFAULT_LIMIT;
    return Math.min(500, Math.max(1, n));
  }, [limit]);

  const buildParams = useCallback(
    (pageNum) => {
      const params = { page: pageNum, limit: limitParsed };
      const ph = phone.trim();
      const em = email.trim();
      const ot = ownerType.trim();
      const c = currency.trim();
      const st = status.trim();
      const wid = walletIdFilter.trim();
      // API: at most one of phone or email — server resolves contact to owner uid (not raw owner_id in query)
      if (ph && !em) params.phone = ph;
      else if (em && !ph) params.email = em;
      if (ot) params.owner_type = ot;
      if (c) params.currency = c;
      if (st) params.status = st;
      if (wid) params.wallet_id = wid;
      return params;
    },
    [phone, email, ownerType, currency, status, walletIdFilter, limitParsed]
  );

  const fetchPage = useCallback(
    /** @param {number} pageNum @param {Record<string, string|number>|null} explicitParams */
    async (pageNum, explicitParams = null) => {
      setLoading(true);
      setError(null);
      try {
        const params = explicitParams ?? buildParams(pageNum);
        const res = await getInternalNonUserWallets(params);
        if (res && typeof res.success === 'boolean' && res.success === false) {
          setRows([]);
          setTotal(0);
          setError(typeof res.message === 'string' ? res.message : 'Request failed.');
          return;
        }
        const list = Array.isArray(res?.wallets) ? res.wallets : [];
        setRows(list);
        setTotal(Number(res?.total) || 0);
        if (typeof res?.page === 'number' && res.page > 0) setPage(res.page);
      } catch (e) {
        console.error(e);
        setRows([]);
        setTotal(0);
        setError(getInternalErrorMessage(e, 'Search Wallet could not load results'));
      } finally {
        setLoading(false);
      }
    },
    [buildParams]
  );

  useEffect(() => {
    if (!listAllowed) return;
    void fetchPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial load when access is confirmed
  }, [listAllowed]);

  const totalPages = total > 0 ? Math.max(1, Math.ceil(total / limitParsed)) : 1;

  const applySearch = (e) => {
    e?.preventDefault?.();
    if (phone.trim() && email.trim()) {
      setError('Enter only one of phone or email.');
      return;
    }
    setPage(1);
    void fetchPage(1);
  };

  const goPrev = () => {
    const next = Math.max(1, page - 1);
    setPage(next);
    void fetchPage(next);
  };

  const goNext = () => {
    const next = Math.min(totalPages, page + 1);
    setPage(next);
    void fetchPage(next);
  };

  const resetFilters = () => {
    setPhone('');
    setEmail('');
    setOwnerType('');
    setCurrency('');
    setStatus('');
    setWalletIdFilter('');
    setLimit(String(DEFAULT_LIMIT));
    setPage(1);
    void fetchPage(1, { page: 1, limit: DEFAULT_LIMIT });
  };

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await logoutRemote();
    } finally {
      setLoggingOut(false);
      navigate('/login', { replace: true });
    }
  };

  const showAccessDeniedCard =
    pingStatus === 'skipped' || (pingStatus === 'ready' && !listAllowed);

  return (
    <div>
      <header className="mb-8">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-slate-600">{getWelcomeGreeting()}</p>
          <button
            type="button"
            onClick={handleLogout}
            disabled={loggingOut}
            className="btn-secondary inline-flex items-center gap-2 text-sm"
          >
            <i className={`material-icons-outlined text-base ${loggingOut ? 'animate-spin' : ''}`}>
              {loggingOut ? 'refresh' : 'logout'}
            </i>
            {loggingOut ? 'Signing out…' : 'Log out'}
          </button>
        </div>
        <Link to="/wallets" className="link-back mb-2">
          <i className="material-icons-outlined text-base">arrow_back</i>
          All wallets
        </Link>
        <div className="flex gap-4">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-primary-500 to-primary-700 text-white shadow-lg shadow-primary-500/25">
            <i className="material-icons-outlined text-3xl">search</i>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-primary-600">Internal API</p>
            <h1 className="page-title">Search Wallet</h1>
            <p className="page-subtitle">
              Find organisation and system wallets through the internal API (VPN). Optional phone or email scopes results
              to that account&apos;s owner (resolved server-side — use one contact, not both). Refine with owner type,
              currency, status, or wallet id, then open a row for its statement.
            </p>
          </div>
        </div>
      </header>

      {pingStatus === 'loading' ? (
        <div className="card-surface p-8">
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-10 animate-pulse rounded-lg bg-slate-100" />
            ))}
          </div>
        </div>
      ) : null}

      {showAccessDeniedCard ? (
        <div className="card-surface p-6">
          <div className="flex items-start gap-3 border-l-4 border-amber-500 bg-amber-50 p-4 text-amber-950">
            <i className="material-icons-outlined shrink-0">vpn_lock</i>
            <div>
              <p className="font-medium">Search Wallet is not available</p>
              <p className="mt-1 text-sm opacity-90">
                {pingStatus === 'skipped'
                  ? 'The internal wallet API URL is not configured for this build.'
                  : 'Connect to VPN and ensure your account is allowed to use Search Wallet, then try again.'}
              </p>
              <Link to="/wallets" className="mt-3 inline-flex text-sm font-medium text-primary-700 hover:underline">
                Back to your wallets
              </Link>
            </div>
          </div>
        </div>
      ) : null}

      {listAllowed ? (
        <>
          <form onSubmit={applySearch} className="card-surface mb-6 border border-slate-100 p-5">
            <p className="mb-3 text-xs text-slate-600">
              Contact scope: use <strong className="font-medium text-slate-700">either</strong> phone or email to limit
              results to that account&apos;s owner — not both. Other filters apply on top.
            </p>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-7">
              <label className="flex flex-col gap-1 text-xs">
                <span className="font-semibold uppercase tracking-wide text-slate-500">Phone</span>
                <input
                  type="text"
                  value={phone}
                  onChange={(e) => {
                    const v = e.target.value;
                    setPhone(v);
                    if (v.trim()) setEmail('');
                  }}
                  placeholder="E.164 or local number"
                  className="rounded-lg border border-slate-200 bg-white px-2.5 py-2 font-mono text-xs text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  autoComplete="off"
                />
              </label>
              <label className="flex flex-col gap-1 text-xs">
                <span className="font-semibold uppercase tracking-wide text-slate-500">Email</span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    const v = e.target.value;
                    setEmail(v);
                    if (v.trim()) setPhone('');
                  }}
                  placeholder="Account email"
                  className="rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-xs text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  autoComplete="off"
                />
              </label>
              <label className="flex flex-col gap-1 text-xs">
                <span className="font-semibold uppercase tracking-wide text-slate-500">Owner type</span>
                <input
                  type="text"
                  value={ownerType}
                  onChange={(e) => setOwnerType(e.target.value)}
                  placeholder="e.g. ORGANISATION"
                  className="rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-xs text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  autoComplete="off"
                />
              </label>
              <label className="flex flex-col gap-1 text-xs">
                <span className="font-semibold uppercase tracking-wide text-slate-500">Currency</span>
                <input
                  type="text"
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  placeholder="e.g. CASH, INR"
                  className="rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-xs text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  autoComplete="off"
                />
              </label>
              <label className="flex flex-col gap-1 text-xs">
                <span className="font-semibold uppercase tracking-wide text-slate-500">Status</span>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-xs text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                >
                  {STATUS_OPTIONS.map((o) => (
                    <option key={o.value || 'any'} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1 text-xs">
                <span className="font-semibold uppercase tracking-wide text-slate-500">Wallet ID</span>
                <input
                  type="text"
                  value={walletIdFilter}
                  onChange={(e) => setWalletIdFilter(e.target.value)}
                  placeholder="Substring match"
                  className="rounded-lg border border-slate-200 bg-white px-2.5 py-2 font-mono text-xs text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  autoComplete="off"
                />
              </label>
              <label className="flex flex-col gap-1 text-xs">
                <span className="font-semibold uppercase tracking-wide text-slate-500">Per page</span>
                <input
                  type="number"
                  min={1}
                  max={500}
                  value={limit}
                  onChange={(e) => setLimit(e.target.value)}
                  className="rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-xs text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </label>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <button type="submit" className="btn-primary inline-flex items-center gap-1.5 text-sm" disabled={loading}>
                <i className={`material-icons-outlined text-base ${loading ? 'animate-spin' : ''}`}>
                  {loading ? 'refresh' : 'search'}
                </i>
                {loading ? 'Loading…' : 'Search'}
              </button>
              <button type="button" className="btn-secondary text-sm" disabled={loading} onClick={resetFilters}>
                Reset
              </button>
            </div>
          </form>

          <div className="card-surface overflow-hidden p-5">
            {error && (
              <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                <i className="material-icons-outlined shrink-0 text-base">error_outline</i>
                <span>{error}</span>
              </div>
            )}
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-600">
              <span>
                {total > 0 ? (
                  <>
                    Showing page {page} of {totalPages} ({total} wallet{total === 1 ? '' : 's'})
                  </>
                ) : (
                  <>No results yet — set filters above and run search.</>
                )}
              </span>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className="btn-secondary py-1 pl-2 pr-2.5 text-xs"
                  disabled={loading || page <= 1}
                  onClick={goPrev}
                >
                  Previous
                </button>
                <button
                  type="button"
                  className="btn-secondary py-1 pl-2 pr-2.5 text-xs"
                  disabled={loading || page >= totalPages}
                  onClick={goNext}
                >
                  Next
                </button>
              </div>
            </div>

            <div className="overflow-x-auto rounded-lg border border-slate-100">
              <table className="min-w-full divide-y divide-slate-100 text-sm">
                <thead>
                  <tr className="bg-slate-50/90 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                    <th className="whitespace-nowrap px-3 py-3">Wallet ID</th>
                    <th className="whitespace-nowrap px-3 py-3">Owner type</th>
                    <th className="whitespace-nowrap px-3 py-3">Owner name</th>
                    <th className="whitespace-nowrap px-3 py-3">Currency</th>
                    <th className="whitespace-nowrap px-3 py-3">Status</th>
                    <th className="whitespace-nowrap px-3 py-3 text-right">Available</th>
                    <th className="whitespace-nowrap px-3 py-3 text-right">Credit limit</th>
                    <th className="whitespace-nowrap px-3 py-3 text-right">Credit in use</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {!loading && rows.length === 0 && !error && (
                    <tr>
                      <td colSpan={8} className="px-3 py-10 text-center text-slate-500">
                        No wallets match this search.
                      </td>
                    </tr>
                  )}
                  {loading && (
                    <tr>
                      <td colSpan={8} className="px-3 py-10 text-center text-slate-500">
                        <i className="material-icons-outlined mr-1 inline-block animate-spin align-middle text-base">
                          refresh
                        </i>{' '}
                        Loading…
                      </td>
                    </tr>
                  )}
                  {!loading &&
                    rows.map((row, index) => {
                      const wid = row?.wallet_id;
                      const rowCurrency =
                        row?.currency != null && String(row.currency).trim() !== ''
                          ? String(row.currency).trim()
                          : 'INR';
                      const canOpen = wid != null && String(wid).trim() !== '';
                      return (
                        <tr
                          key={String(wid ?? index)}
                          role={canOpen ? 'button' : undefined}
                          tabIndex={canOpen ? 0 : -1}
                          onClick={() => {
                            if (canOpen) navigate(`/wallets/${encodeURIComponent(String(wid).trim())}/statement`);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              if (canOpen) navigate(`/wallets/${encodeURIComponent(String(wid).trim())}/statement`);
                            }
                          }}
                          className={
                            canOpen
                              ? 'cursor-pointer transition-colors hover:bg-slate-50/80'
                              : 'cursor-not-allowed opacity-70'
                          }
                        >
                          <td className="max-w-[14rem] break-all px-3 py-2.5 font-mono text-xs text-slate-700">
                            {wid ?? '—'}
                          </td>
                          <td className="px-3 py-2.5 text-slate-800">{row?.owner_type ?? '—'}</td>
                          <td className="max-w-[12rem] break-all px-3 py-2.5 text-xs text-slate-600">
                            {row?.owner_name != null && String(row.owner_name).trim() !== ''
                              ? String(row.owner_name)
                              : '—'}
                          </td>
                          <td className="whitespace-nowrap px-3 py-2.5 text-slate-700">
                            {String(rowCurrency).toUpperCase() === 'CASH' ? 'CASH (credit)' : rowCurrency}
                          </td>
                          <td className="whitespace-nowrap px-3 py-2.5 text-slate-700">{row?.status ?? '—'}</td>
                          <td className="whitespace-nowrap px-3 py-2.5 text-right font-semibold tabular-nums text-emerald-700">
                            {formatWalletBalanceMinor(row?.balance, rowCurrency)}
                          </td>
                          <td className="whitespace-nowrap px-3 py-2.5 text-right tabular-nums text-slate-700">
                            {formatWalletBalanceMinor(row?.credit_line_limit_minor, rowCurrency)}
                          </td>
                          <td className="whitespace-nowrap px-3 py-2.5 text-right font-semibold tabular-nums text-red-700">
                            {formatWalletBalanceMinor(row?.credit_line_depleted_minor, rowCurrency)}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : null}

      {pingStatus === 'unreachable' ? (
        <InternalApiUnreachable title="Search Wallet could not reach the internal API" onRetry={() => runPing()} />
      ) : null}
    </div>
  );
}

export default NonUserWalletsPage;
